
import { LogLevel, LogCategory } from './types/logs';
import { FC } from 'react';

// ========= ENUMS =========

export enum UserRole {
  ADMIN = 'ADMIN',
  SYSTEM_ADMIN_LIVE = 'SYSTEM_ADMIN_LIVE',
  ARTIST = 'ARTIST',
  VENUE = 'VENUE',
  SPONSOR = 'SPONSOR',
  REVELLER = 'REVELLER',
  ORGANIZER = 'ORGANIZER',
  DAO_MEMBER = 'DAO_MEMBER',
  SERVICE_PROVIDER = 'SERVICE_PROVIDER',
  DAO_GOVERNOR = 'DAO_GOVERNOR',
  GUEST = 'GUEST',
}

export enum UserStatus {
    PENDING_PASSWORD_SETUP = 'PENDING_PASSWORD_SETUP',
    ACTIVE = 'ACTIVE',
    INACTIVE = 'INACTIVE',
    SUSPENDED = 'SUSPENDED',
}

export enum ContractStatus {
  PENDING = "PENDING",
  SIGNED = "SIGNED",
  ACTIVE = "ACTIVE",
  FULFILLED = "FULFILLED",
  DISPUTED = "DISPUTED",
  CANCELED = "CANCELED",
}

export enum ContractType {
  GIG = "GIG",
  SERVICE = "SERVICE",
}

export enum OnboardingStatus {
  PENDING = "PENDING",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
}

export enum ProposalStatus {
  DRAFT = "DRAFT",
  PENDING = "PENDING",
  ACTIVE = "ACTIVE",
  ACCEPTED = "ACCEPTED",
  PASSED = "PASSED",
  FAILED = "FAILED",
  EXECUTED = "EXECUTED",
  Vetoed = 'Vetoed',
  Admin_Passed = 'Admin_Passed',
  REJECTED = "REJECTED",
  DENIED = "DENIED",
  NEGOTIATION = "NEGOTIATION",
  FUNDED = "FUNDED",
  FULFILLED = "FULFILLED",
}

export enum EscrowStatus {
  CREATED = "CREATED",
  FUNDED = "FUNDED",
  RELEASED = "RELEASED",
  REFUNDED = "REFUNDED",
  DISPUTED = "DISPUTED",
}

export enum EventTicketStatus {
  VALID = "VALID",
  INVALIDATED = "INVALIDATED",
  CHECKED_IN = "CHECKED_IN",
}

/**
 * @deprecated This enum is obsolete and will be removed. Use MarketplaceItemStatus instead.
 */
export enum ItemStatus {
  AVAILABLE = "AVAILABLE",
  SOLD = "SOLD",
  REMOVED = "REMOVED",
}

export enum EventStatus {
  SCHEDULED = "SCHEDULED",
  CANCELLED = "CANCELLED",
  PENDING = "PENDING",
}

export enum HRStatus {
  ACTIVE = "ACTIVE",
  ON_LEAVE = "ON_LEAVE",
  INACTIVE = "INACTIVE",
}

export enum ApprovalStatus {
  PENDING = 'Pending',
  APPROVED = 'Approved',
  REJECTED = 'Rejected',
}

export enum TicketPriority {
    LOW = 'Low',
    MEDIUM = 'Medium',
    HIGH = 'High',
    CRITICAL = 'Critical',
}

export enum TransactionStatus {
    COMPLETED = 'Completed',
    PENDING = 'Pending',
    FAILED = 'Failed',
}

export enum SupportTicketStatus {
    OPEN = 'Open',
    IN_PROGRESS = 'In Progress',
    RESOLVED = 'Resolved',
    CLOSED = 'Closed',
}

export enum SupportTicketCategory {
  TRANSACTION_ESCROW_ISSUE = 'Transaction/Escrow Issue',
  PROPOSAL_DISPUTE = 'Proposal Dispute',
  PAYMENT_GATEWAY_FAILURE = 'Payment Gateway Failure',
  PLATFORM_BUG_GLITCH = 'Platform Bug/Glitch',
  ACCOUNT_VERIFICATION = 'Account Verification',
  GENERAL = 'General Inquiry',
  ZERO_TOLERANCE_APPEAL = 'Zero Tolerance Appeal',
}

export enum SupportTicketPriority {
  LOW = 'Low',
  MEDIUM = 'Medium',
  HIGH = 'High',
  URGENT = 'Urgent',
}

// ADDED: Enums to match backend Prisma schema
export enum MarketplaceItemStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  SOLD = 'SOLD',
}

export enum OrderStatus {
  PAYMENT_PENDING = 'PAYMENT_PENDING',
  COMPLETED = 'COMPLETED',
  SHIPPED = 'SHIPPED',
  DELIVERED = 'DELIVERED',
  CANCELLED = 'CANCELLED',
  REFUNDED = 'REFUNDED',
  DISPUTED = 'DISPUTED',
}

export enum TransactionType {
  INCOME = 'INCOME',
  EXPENSE = 'EXPENSE',
}


// ========= AUTH & CORE USER MODELS =========

export interface Account {
  id: string;
  userId: string;
  type: string;
  provider: string;
  providerAccountId: string;
  refresh_token?: string | null;
  access_token?: string | null;
  expires_at?: number | null;
  token_type?: string | null;
  scope?: string | null;
  id_token?: string | null;
  session_state?: string | null;
  user: User;
}

export interface Session {
  id: string;
  sessionToken: string;
  userId: string;
  expires: Date;
  user: User;
}

export interface User {
  id: string;
  name?: string | null;
  email?: string | null;
  emailVerified?: Date | null;
  image?: string | null;
  role: UserRole;
  accounts?: Account[];
  sessions?: Session[];
  lead?: Lead | null;
  onboardingRequest?: OnboardingRequest | null;
  artistProfile?: ArtistProfile | null;
  venueProfile?: VenueProfile | null;
  sponsorProfile?: SponsorProfile | null;
  organizerProfile?: OrganizerProfile | null;
  serviceProviderProfile?: ServiceProviderProfile | null;
  daoMemberProfile?: DaoMemberProfile | null;
  daoGovernorProfile?: DaoGovernorProfile | null; 
  revellerProfile?: RevellerProfile | null; 
  gigsAsArtist?: Gig[];
  gigsAsVenue?: Gig[];
  initiatedContracts?: Contract[];
  signedContracts?: Contract[];
  proposals?: Proposal[];
  votes?: Vote[];
  ownedNFTs?: NFT[];
  ownedTickets?: EventTicket[];
  ownedFractions?: FractionalTokenOwner[];
  escrowsAsParty?: EscrowContract[];
  treasury?: TreasuryTransaction[];
  avatar?: string;
  location?: string;
  bio?: string;
  xp?: number;
  level?: number;
  password?: string;
  walletAddress?: string;
  phone?: string;
  instagram?: string;
  x?: string;
}

export interface VerificationToken {
  identifier: string;
  token: string;
  expires: Date;
}

// ========= USER ONBOARDING =========

export interface OnboardingRequest {
    id: string;
    name: string;
    email: string;
    type: 'Lead' | 'Join Request';
    date: Date;
    status: OnboardingStatus;
    requestedRole: UserRole;
    role?: UserRole;
    location?: string;
    bio?: string;
    notes?: string;
    user?: Pick<User, 'id' | 'name' | 'role'> | null;
    userId?: string;
    submittedAt?: Date;
  }

// ========= USER PROFILES =========

export interface ArtistProfile extends User {
  name: string;
  avatar: string;
  role: UserRole;
  xp: number;
  level: number;
  password?: string;
  coverImage: string;
  description: string;
  location: string;
  genres: string[];
  verified: boolean;
  followers?: CircleMember[];
  businessAssociates?: CircleMember[];
  pressKit: {
    photos: string[];
    topTracks: { title: string; duration: string; plays: string }[];
    techRiderUrl: string;
    socials: { platform: string; followers: string }[];
  };
  stats: {
    gigsCompleted: number;
    activeGigs: number;
    rating: number;
    responseTime: string;
  };
  equityOpportunities?: EquityOpportunity[];
  subscription?: {
    planName: string;
    status: 'Active' | 'Expired' | 'Grace Period';
    expiryDate: string;
    supportTier: 'Standard' | 'Priority' | 'Elite';
    autoRenew: boolean;
    hasLeadGeniusSync?: boolean;
  };
  savedPaymentMethods?: {
    crypto: {
      id: string;
      network: string;
      address: string;
      label: string;
      isDefault?: boolean;
    }[];
    fiat: {
      id: string;
      type: 'Card' | 'Bank';
      last4: string;
      label: string;
      expiry?: string;
      isDefault?: boolean;
    }[];
  };
  revenue?: RevenueStats;
  leadQueries?: LeadQuery[];
  status?: ApprovalStatus;
  musicBrainzId?: string;
  source?: string;
  generatedDate?: string;
  notes?: string;
  leadStatus?: 'New' | 'Contacted' | 'Closed' | 'Converted';
  genre?: string | null;
  techRiderUrl?: string | null;
  portfolioUrl?: string | null;
  user?: User;
  userId?: string;
  onboardingComplete?: boolean;
  isMock?: boolean;
}

export interface VenueProfile {
  id: string;
  user: User;
  userId: string;
  location?: string | null;
  capacity?: number | null;
  amenities: string[];
  verified: boolean;
  website?: string;
  venueType?: string;
  companyName?: string;
  companyRegistrationDetails?: string;
}

export interface SponsorProfile {
  id: string;
  user: User;
  userId: string;
  companyName?: string | null;
  website?: string | null;
  focusAreas: string[];
  verified: boolean;
}

export interface OrganizerProfile {
  id: string;
  user: User;
  userId: string;
  organization?: string | null;
  pastEvents: string[];
  verified: boolean;
}

export interface ServiceProviderProfile {
  id: string;
  user: User;
  userId: string;
  serviceType: string;
  rate?: string | null;
  availability?: string | null;
  verified: boolean;
}

export interface DaoMemberProfile {
  id: string;
  user: User;
  userId: string;
  votingPower: number;
  joinedAt: Date;
  verified: boolean;
}

export interface DaoGovernorProfile {
    id: string;
    user: User;
    userId: string;
    proposals_created: number;
    votes_cast: number;
    verified: boolean;
}

export interface RevellerProfile {
    id: string;
    user: User;
    userId: string;
    tickets_purchased: number;
    verified: boolean;
}


// ========= CORE BUSINESS LOGIC =========

export interface Gig {
  id: string;
  title: string;
  description?: string | null;
  date: Date;
  status: string;
  artist: User | ArtistProfile;
  artistId: string;
  venue: User | VenueProfile | ArtistProfile;
  venueId: string;
  contracts: Contract[];
  event?: Event | null;
}

export interface Contract {
  id: string;
  name: string;
  terms: string;
  status: ContractStatus;
  type: ContractType;
  completionNote?: string | null;
  gig?: Gig | null;
  gigId?: string | null;
  initiator: User;
  initiatorId: string;
  parties: User[];
  escrow?: EscrowContract | null;
  createdAt: Date;
  updatedAt: Date;
}

// ========= DAO & GOVERNANCE =========

export interface Proposal {
  id: string;
  title: string;
  description: string;
  status: ProposalStatus | null;
  proposer?: User;
  proposerId?: string;
  votes?: Vote[];
  createdAt?: Date;
  updatedAt?: Date;
  votesFor?: number;
  votesAgainst?: number;
  deadline?: string;
  creator?: string;
  quorumRequired?: number;
  currentParticipation?: number;
  isCritical?: boolean;
  contractData?: SmartContractDraft;
  isMock?: boolean;
}

export type DAOProposal = Proposal;

export interface Vote {
  id: string;
  proposal: Proposal;
  proposalId: string;
  voter: User;
  voterId: string;
  choice: boolean;
  votingPower: number;
  createdAt: Date;
}

export interface TreasuryTransaction {
  id: string;
  type: string;
  amount: number;
  timestamp: string;
}


// ========= SMART CONTRACT & WEB3 MODELS =========

export interface Event {
  id: string;
  name: string;
  venueName?: string;
  gig?: Gig;
  gigId?: string;
  tickets?: EventTicket[];
  status: EventStatus;
  startTime: Date;
  endTime: Date;
  contractAddress?: string | null;
}

export interface EventTicket {
  id: string; 
  event?: Event;
  eventId: string;
  tokenId: string;
  owner?: User;
  ownerId: string;
  quantity: number; 
  status: EventTicketStatus;
  contractAddress?: string;
  onChain?: boolean;
}

export interface NFT {
  id: string;
  owner: User;
  ownerId: string;
  tokenId: number;
  contractAddress: string;
  metadataUrl: string;
  fractionalization?: Fractionalize | null;
}

export interface Fractionalize {
  id: string;
  nft: NFT;
  nftId: string;
  tokenAddress: string;
  totalSupply: bigint;
  tokenOwners: FractionalTokenOwner[];
}

export interface FractionalTokenOwner {
  id: string;
  fractionalize: Fractionalize;
  fractionalizeId: string;
  owner: User;
  ownerId: string;
  balance: bigint;
}

export interface EscrowContract {
  id: string;
  contract?: Contract;
  contractId?: string;
  escrowAddress?: string;
  status: EscrowStatus;
  amount: number; 
  parties: User[];
  collabProposal?: CollabProposal | null; 
  collabProposalId?: string | null;
}

export interface Lead {
  id: string;
  user: User | null;
  userId: string;
  status: string;
  createdAt: Date;
  updatedAt: Date;
  name?: string;
  bio?: string;
}

// UPDATED: MarketplaceItem to align with fixed-price backend schema and preserve auction fields as optional.
export interface MarketplaceItem {
  id: string;
  title: string;
  description?: string;
  price: number;
  currency: 'ETH' | 'USD' | 'KALA' | 'USDC' | 'EUR' | 'MATIC' | 'INR';
  type: 'NFT' | 'Physical Art' | 'Ticket' | 'Instrument' | 'Equipment';
  image?: string; // This will now store the GCS object name
  imageUrl?: string; // This will store the temporary signed URL for display
  status: MarketplaceItemStatus;
  quantity: number;
  sellerId: string;
  seller: {
    name: string;
    avatar?: string;
  };
  createdAt: string; 
  updatedAt: string;
  // Preserving fields as optional for future auction feature, as requested.
  isAuction?: boolean;
  endTime?: string;
  condition?: 'New' | 'Like New' | 'Good' | 'Fair' | 'Digital';
  className?: string; // Keep className as it was in the original
}

// ADDED: FiatTransaction for order details
export interface FiatTransaction {
    id: string;
    amount: number;
    currency: string;
    status: string;
    description?: string | null;
    type: TransactionType;
    referenceId?: string | null;
    notes?: string | null;
    userId?: string | null;
    createdAt: string;
    updatedAt: string;
    marketplaceOrderId?: string | null;
}

// ADDED: MarketplaceOrder for purchase/sale history
export interface MarketplaceOrder {
    id: string;
    itemId: string;
    item: MarketplaceItem;
    buyerId: string;
    buyer?: { 
        id: string;
        name: string;
    };
    status: OrderStatus;
    quantity: number;
    priceAtPurchase: number;
    transactionId: string;
    transaction: FiatTransaction;
    createdAt: string;
    updatedAt: string;
}

export interface HumanResources {
    id: string;
    name: string;
    role: string;
    status: HRStatus;
    joinDate: Date;
    salary: number;
    tasksAssigned: string[];
}

// ADDED: A new, non-destructive type for a single reply.
export interface ForumReply {
  id: string;
  threadId: string;
  authorId: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  author?: string;
  authorAvatar?: string;
}

export interface ForumThread {
  id: string;
  authorId: string;
  title: string;
  category: 'Collaboration' | 'Gear Swap' | 'General' | 'Showcase';
  author: string;
  authorAvatar: string;
  replies: number;
  views: number;
  lastActive: string;
  isPinned?: boolean;
}

export interface Notification {
  id: string;
  userId: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warn';
  read: boolean;
  timestamp: string;
}

export interface SmartContractDraft {
  id: string;
  contractType: 'IERC-20' | 'IERC-721' | 'Service Agreement' | 'KalaKrutToken' | 'TimelockController' | 'KalaKrutGovernor' | 'Treasury' | 'KalaKrutNFT' | 'EventTicket' | 'Fractionalizer' | 'Escrow' | 'ServiceAgreement';
  content: string;
  lastEditedBy: 'User' | 'Admin';
  version: number;
  status: 'Draft' | 'Pending Review' | 'Negotiation' | 'Active' | 'Approved' | 'Rejected' | 'Published';
  adminNotes?: string;
  parameters?: string[];
}

export interface RevenueStats {
  totalLifetime: number;
  pendingPayout: number;
  lastMonth: number;
  breakdown: {
    gigs: number;
    merch: number;
    royalties: number;
    licensing: number;
  };
  recentPayouts: {
    date: string;
    amount: number;
    method: string;
    status: 'Completed' | 'Processing';
  }[];
}

export interface EquityOpportunity {
  id:string;
  title: string;
  type: 'Project Equity' | 'Career Equity' | 'Asset Fractionalization';
  description: string;
  totalValuation: number;
  currency: 'USD' | 'ETH';
  equityAvailablePercentage: number;
  minInvestment: number;
  tokenSymbol?: string;
  technology: 'ERC-20' | 'ERC-1155' | 'Legal DAO Wrapper';
  termsSummary: string;
  riskLevel: 'Low' | 'Medium' | 'High';
  backersCount: number;
}

export interface LeadQuery {
  id: string;
  date: string;
  query: string;
  responseSummary: string;
  method: 'Auto-Sync' | 'Manual Entry';
  isPaidService: boolean;
}

export interface CircleMember {
  id: string;
  name: string;
  avatar: string;
  role: string;
}

export interface RosterMemberAsset {
  ips: string[];
  contents: string[];
  events: string[];
  products: string[];
  services: string[];
  equipment: string[];
  instruments: string[];
  tickets: string[];
  productions?: string[];
  nfts?: string[];
}

export interface RosterMember {
  id: string;
  name: string;
  role: UserRole;
  avatar: string;
  location: string;
  verified: boolean;
  walletAddress?: string;
  bio?: string;
  assets: RosterMemberAsset;
  rating: number;
  subscriberOnly: {
    email: string;
    phone: string;
    agentContact: string;
  };
  isMock?: boolean;
  password?: string;
  protected?: boolean;
  status?: UserStatus;
}

export interface Message {
  id: string;
  sender: 'me' | 'them' | 'system';
  text: string;
  timestamp: string;
  isWarning?: boolean;
}

export interface LeaderboardEntry {
  rank: number;
  user: User;
  badges: string[];
  change: number;
}

export interface Milestone {
  id: string;
  task: string;
  percentage: number;
  status: 'Pending' | 'Submitted' | 'Released';
  proof?: string;
  totalRate?: number;
  deadline?: string;
}

export interface CollabProposal {
  description: string;
  chatHistory: any[];
  id: string;
  eventName: string;
  artistId: string;
  artistName: string;
  proposerId: string;
  status: ProposalStatus;
  budget: string;
  currency: 'USD' | 'ETH' | 'KALA' | 'INR' | 'EUR' | 'MATIC';
  receiverNote?: string;
  sentDate: string;
  paymentType: 'Lump Sum' | 'Milestone Based';
  milestones?: Milestone[];
  isUrgent?: boolean;
  contractData?: SmartContractDraft;
  escrow?: EscrowContract;
  escrowId?: string;
}

export interface UrgentBookingRequestDetails {
  eventName: string;
  daysUntilEvent: string;
  totalRate: number;
  currency: 'USD' | 'ETH' | 'KALA' | 'INR' | 'EUR' | 'MATIC';
  paymentType: 'Lump Sum' | 'Milestone Based';
  milestones?: Milestone[];
  message?: string;
  artistName: string;
}

export interface UrgentBooking {
    id: string;
    artistName: string;
    eventType: string;
    eventDate: string;
    venue: string;
    budget: number;
}

export interface Booking {
  id: string;
  artistName: string;
  venueName: string;
  date: string;
  status: 'Pending' | 'Confirmed' | 'Completed';
  payoutAmount: string;
}

export interface ServiceListing {
  id: string;
  title: string;
  provider: string;
  category:
    | 'Consultancy'
    | 'Grant Writing'
    | 'Legal'
    | 'Marketing'
    | 'Production'
    | 'Platform';
  rate: string;
  rating: number;
  reviews: number;
  isPlatformService?: boolean;
  externalLink?: string;
}

export interface Article {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  date: string;
  author: string;
  image: string;
  category: 'Announcement' | 'Press Release' | 'Community' | 'Feature';
}

export interface Transaction {
  id: string;
  userId: string;
  details: any;
  type: 'Incoming' | 'Outgoing' | 'Mint' | 'Gas' | 'Swap';
  description: string;
  amount: string;
  currency: 'ETH' | 'KALA' | 'USDC' | 'EUR' | 'MATIC' | 'INR';
  date: string;
  status: TransactionStatus;
  hash: string;
  contractId?: string;
  contract?: Contract;
  nftId?: string;
  nft?: NFT;
  fractionalizationId?: string;
  fractionalization?: Fractionalize;
  eventId?: string;
  event?: Event;
  createdAt: string; 
  updatedAt: string;
}

export interface IPFSAsset {
  id: string;
  name: string;
  type: 'Image' | 'Audio' | 'Video' | 'Document';
  size: string;
  cid: string;
  url: string;
  status: 'Uploading' | 'Pinned' | 'Minted' | 'Fractionalized';
  timestamp?: string;
  contractAddress?: string;
  tokenId?: string | number;
  fractionalizerAddress?: string;
}

export interface SupportTicket {
  id: string;
  userId: string;
  userName: string;
  user: {
    id: string;
    name: string;
  }
  subject: string;
  category: SupportTicketCategory;
  priority: SupportTicketPriority;
  status: SupportTicketStatus;
  tier: 'Tier 1' | 'Tier 2' | 'Tier 3';
  createdAt: string;
  lastUpdate: string;
  description?: string;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  price: string;
  period: string;
  features: string[];
  isRecommended?: boolean;
  supportTier: 'Standard' | 'Priority' | 'Elite';
}

export interface TreasuryAsset {
  symbol: string;
  name: string;
  balance: number;
  valueUsd: number;
  allocation: number;
  trend: 'up' | 'down' | 'neutral';
}

export interface StaffMember {
  id: string;
  name: string;
  role: 'Admin' | 'Moderator' | 'Support' | 'Developer' | 'Community Manager';
  department: 'Operations' | 'Tech' | 'Community' | 'Legal';
  status: 'Active' | 'On Leave' | 'Terminated';
  avatar: string;
  email: string;
  lastActive: string;
  designation: string;
  employmentDate: string;
  salary: number;
  currency: string;
  taxDeductions: number;
  normalHours: number;
  hoursWorked: number;
  overtimeHours: number;
  overtimePaid: number;
  leavesAccrued: number;
  leavesUsed: number;
  duties: string[];
  monthlyTasks: string[];
}

export interface ModerationCase {
  id: string;
  userId: string;
  userName: string;
  userRole: UserRole;
  violationType: string;
  contentSnippet: string;
  status:
    | 'Blocked'
    | 'Appeal Pending'
    | 'Resolved - Unblocked'
    | 'Resolved - Ban Upheld';
  timestamp: string;
  appealReason?: string;
}

export interface NavItem {
    label: string;
    view: string;
    icon: FC<{ className: string }>; 
}

export interface Artist {
  id: string;
  name: string;
  bio: string;
  genres?: string[];
}

export interface JoinRequest {
  id: string;
  user: ArtistProfile;
  date: string;
  status: ApprovalStatus;
  location?: string;
}

export interface Log {
  id: string;
  timestamp: number;
  level: LogLevel;
  category: LogCategory;
  message: string;
  userId?: string;
  data?: Record<string, unknown>;
}

export interface UiComponent {
    name: string;
    type: string;
    filePath: string;
    description?: string;
    subComponents?: UiComponent[];
}

export { LogLevel, LogCategory } from './types/logs';
